import { Link } from "react-router-dom";
import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.post("/newsletter/subscribe", { email, source: "footer" });
      toast.success("You're on the list.");
      setEmail("");
    } catch { toast.error("Could not subscribe"); }
    finally { setBusy(false); }
  };
  return (
    <footer className="bg-brand-ink text-white mt-24">
      {/* Newsletter strip */}
      <div className="border-b border-white/10">
        <div className="container-narrow py-10 grid md:grid-cols-12 gap-6 items-center">
          <div className="md:col-span-6">
            <span className="overline text-brand-rust">Field Notes — twice a month</span>
            <p className="font-display text-2xl md:text-3xl mt-2 leading-snug">Leadership notes from the fireground, in your inbox.</p>
          </div>
          <form onSubmit={subscribe} className="md:col-span-6 flex gap-2" data-testid="footer-newsletter">
            <input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              data-testid="footer-newsletter-email"
              className="flex-1 bg-transparent border border-white/20 text-white placeholder-white/40 px-4 py-3 text-sm focus:outline-none focus:border-white"
            />
            <button type="submit" disabled={busy} data-testid="footer-newsletter-submit"
              className="bg-brand-rust hover:bg-brand-rustDark text-white px-6 py-3 text-sm font-medium tracking-wide transition-colors disabled:opacity-60">
              {busy ? "…" : "Subscribe"}
            </button>
          </form>
        </div>
      </div>

      <div className="container-narrow py-16 grid md:grid-cols-5 gap-10">
        <div className="md:col-span-2">
          <div className="flex items-center gap-2">
            <span className="inline-block w-2 h-2 bg-brand-rust" />
            <span className="font-serif text-2xl">South on Leadership</span>
          </div>
          <p className="mt-4 text-sm text-white/65 max-w-md leading-relaxed">
            A small, deliberate library of books and courses for leaders who'd rather build durable teams than chase the next quarter.
          </p>
          <div className="mt-6 flex items-center gap-3" data-testid="footer-socials">
            <a href="https://www.instagram.com/south_onleadership" target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="social-instagram"
              className="w-10 h-10 inline-flex items-center justify-center border border-white/15 text-white/70 hover:text-white hover:border-brand-rust hover:bg-brand-rust/10 transition-colors">
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
            </a>
            <a href="https://m.youtube.com/@southonleadership" target="_blank" rel="noopener noreferrer" aria-label="YouTube" data-testid="social-youtube"
              className="w-10 h-10 inline-flex items-center justify-center border border-white/15 text-white/70 hover:text-white hover:border-brand-rust hover:bg-brand-rust/10 transition-colors">
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"/><polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"/></svg>
            </a>
            <a href="https://www.tiktok.com/@robbie.on.leadership" target="_blank" rel="noopener noreferrer" aria-label="TikTok" data-testid="social-tiktok"
              className="w-10 h-10 inline-flex items-center justify-center border border-white/15 text-white/70 hover:text-white hover:border-brand-rust hover:bg-brand-rust/10 transition-colors">
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.83a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.26z"/></svg>
            </a>
            <a href="https://open.spotify.com/show/5BIKkX13rvdHq4falhhmRu" target="_blank" rel="noopener noreferrer" aria-label="Podcast on Spotify" data-testid="social-podcast"
              className="w-10 h-10 inline-flex items-center justify-center border border-white/15 text-white/70 hover:text-white hover:border-brand-rust hover:bg-brand-rust/10 transition-colors">
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="2" width="6" height="12" rx="3"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="22"/></svg>
            </a>
          </div>
        </div>
        <div>
          <h4 className="overline text-white/60 mb-4">Explore</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/books" className="text-white/80 hover:text-white">Books</Link></li>
            <li><Link to="/courses" className="text-white/80 hover:text-white">Courses</Link></li>
            <li><Link to="/bundles" className="text-white/80 hover:text-white">Bundles</Link></li>
            <li><Link to="/field-notes" className="text-white/80 hover:text-white">Field Notes</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="overline text-white/60 mb-4">Brand</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/about" className="text-white/80 hover:text-white">About</Link></li>
            <li><Link to="/speak" className="text-white/80 hover:text-white">Book Robbie</Link></li>
            <li><Link to="/newsletter" className="text-white/80 hover:text-white">Newsletter</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="overline text-white/60 mb-4">Account</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/library" className="text-white/80 hover:text-white">My Library</Link></li>
            <li><Link to="/login" className="text-white/80 hover:text-white">Sign in</Link></li>
            <li><Link to="/register" className="text-white/80 hover:text-white">Create account</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="container-narrow py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-white/50">
          <div className="flex flex-col gap-1">
            <span>© {new Date().getFullYear()} South on Leadership. All rights reserved.</span>
            <span className="text-white/40" data-testid="amazon-disclosure">As an Amazon Associate, South on Leadership earns from qualifying purchases.</span>
          </div>
          <span className="overline">Built for leaders</span>
        </div>
      </div>
    </footer>
  );
}
